import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { TBlockedUser } from '@onesource/schemas';
import { getMediaUrl, useUnblockUserMutation } from '@onesource/services';
import { FC } from 'react';
import { MdLockOpen } from 'react-icons/md';

type TProps = {
  users: TBlockedUser[];
  /** Suppresses the empty message while a block is in flight (a skeleton row stands in). */
  hideEmptyState?: boolean;
};

export const AccountBlockedUsersList: FC<TProps> = ({ users, hideEmptyState }) => {
  const { mutate: unblock, isPending, variables: unblockingId } = useUnblockUserMutation();

  if (users.length === 0) {
    if (hideEmptyState) {
      return null;
    }
    return (
      <Typography variant="body2" color="text.secondary">
        You haven&apos;t blocked anyone.
      </Typography>
    );
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      {users.map(user => {
        const isUnblocking = isPending && unblockingId === user.authUserId;
        return (
          <Box
            key={user.authUserId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              minHeight: 56,
              py: 1,
              borderBottom: theme => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Avatar src={getMediaUrl(user.avatar ?? undefined)} alt={user.name} sx={{ width: 36, height: 36 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {user.name}
              </Typography>
              {user.username && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  @{user.username}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              variant="soft"
              startIcon={isUnblocking ? <CircularProgress size={14} color="inherit" /> : <MdLockOpen />}
              disabled={isPending}
              onClick={() => unblock(user.authUserId)}
            >
              Unblock
            </Button>
          </Box>
        );
      })}
    </Box>
  );
};
